/**
 * Seller "My listings" (web) — Phase 5 marketplace, seller side.
 *
 * Lists the signed-in seller's OWN marketplace listings (GET
 * /marketplace/listings?mine=true) and lets them manage each one:
 *   - Pause / Resume   → PATCH status active ↔ paused
 *   - Withdraw         → PATCH status 'withdrawn' (confirm dialog — irreversible)
 *   - Offers received  → an expandable per-listing section (GET
 *       /marketplace/listings/:id/offers) where a `pending` offer can be
 *       Accepted (mints an order server-side) or Rejected.
 *
 * MONEY: ask price + offer price/total are micro-ZARP (1e-6 ZARP) STRINGS on the
 * wire; we render them through microToZarp() + the @fse/ui <Money> — never
 * Number() for math. The hand-rolled marketplace client (src/api/marketplace.ts)
 * already unwraps the {data:[]} list envelope; we read `.data`.
 *
 * Role: this is a seller surface (coop_admin / admin — harvest-sourced farmer
 * listings were cut for the pilot). The
 * server is the authority (the listings/offers endpoints scope to the caller),
 * so we don't hard-gate here — the sidebar link in _app.tsx scopes the entry.
 *
 * Online-only (no offline outbox on web): a transport / guard failure surfaces
 * as an inline error and we re-fetch on success to keep the row + badges fresh.
 */
import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Icon,
  Money,
  Skeleton,
  Stack,
  StatusPill,
  Text,
  type StatusPillVariant,
} from '@fse/ui';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseApiError } from '../api/client';
import {
  acceptOffer,
  listListingOffers,
  listListings,
  microToZarp,
  patchListingStatus,
  rejectOffer,
  type ListingDto,
  type ListingStatus,
  type OfferDto,
} from '../api/marketplace';

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

// StatusPill variants per listing status (semantic names, mirrors batches).
const LISTING_PILL: Record<ListingStatus, StatusPillVariant> = {
  draft: 'upcoming',
  active: 'active',
  paused: 'upcoming',
  sold: 'sold',
  withdrawn: 'overdue',
};

// StatusPill variants per offer status.
const OFFER_PILL: Record<OfferDto['status'], StatusPillVariant> = {
  pending: 'due',
  accepted: 'sold',
  rejected: 'overdue',
  withdrawn: 'upcoming',
  expired: 'upcoming',
};

const MY_LISTINGS_KEY = ['my-listings'] as const;

function errMsg(err: unknown): string {
  return err instanceof FseApiError ? err.message : String(err);
}

export function MyListingsScreen() {
  const { t } = useTranslation();

  const listingsQuery = useQuery({
    queryKey: MY_LISTINGS_KEY,
    queryFn: ({ signal }) => listListings({ mine: true }, signal),
  });

  const listings = listingsQuery.data?.data ?? [];

  return (
    <Stack spacing={3}>
      <Box>
        <Text variant="headline-medium" as="h2">
          {t('myListings.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {listingsQuery.data
              ? t('myListings.subtitleWithCount', { count: listings.length })
              : t('myListings.subtitle')}
          </Text>
        </Box>
      </Box>

      {listingsQuery.isPending ? (
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={120} />
          <Skeleton variant="rectangular" height={120} />
        </Stack>
      ) : listingsQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => listingsQuery.refetch()}>
              {t('myListings.retry')}
            </Button>
          }
        >
          {t('myListings.loadError', { error: errMsg(listingsQuery.error) })}
        </Alert>
      ) : listings.length === 0 ? (
        <Card>
          <CardContent>
            <Text variant="title-medium" as="h3">
              {t('myListings.empty.title')}
            </Text>
            <Box sx={{ mt: 1 }}>
              <Text variant="body-medium" color="text.secondary">
                {t('myListings.empty.body')}
              </Text>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {listings.map((listing) => (
            <ListingCard key={listing.id} listing={listing} t={t} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

interface ListingCardProps {
  listing: ListingDto;
  t: TFunction;
}

function ListingCard({ listing, t }: ListingCardProps) {
  const queryClient = useQueryClient();
  const [offersOpen, setOffersOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);

  const askPerKg = microToZarp(listing.askPriceZarpPerKgMicro);
  const isActive = listing.status === 'active';
  const isPaused = listing.status === 'paused';
  const isWithdrawn = listing.status === 'withdrawn';

  function invalidateListings() {
    queryClient.invalidateQueries({ queryKey: MY_LISTINGS_KEY });
  }

  const statusMutation = useMutation({
    mutationFn: (next: ListingStatus) => patchListingStatus(listing.id, next),
    onSuccess: () => {
      setWithdrawOpen(false);
      invalidateListings();
    },
  });

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          {/* Header row: product + status + ask price */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Text variant="title-medium" as="span">
                  {listing.product}
                </Text>
                <StatusPill variant={LISTING_PILL[listing.status]}>
                  {t(`myListings.status.${listing.status}`)}
                </StatusPill>
              </Stack>
              <Text variant="body-medium" color="text.secondary">
                {t('myListings.qty', { qty: listing.qtyKg })}
                {listing.grade ? ` · ${t('myListings.grade', { grade: listing.grade })}` : ''}
                {' · '}
                {t('myListings.listed', { date: dateFormatter.format(new Date(listing.createdAt)) })}
              </Text>
            </Stack>
            <Box sx={{ textAlign: 'right' }}>
              <Text variant="label-medium" color="text.secondary">
                {t('myListings.askPrice')}
              </Text>
              <Box sx={{ mt: 0.25 }}>
                <Money amount={askPerKg} unit={t('myListings.perKg')} size="md" />
              </Box>
            </Box>
          </Box>

          {statusMutation.isError ? (
            <Alert severity="error">
              {t('myListings.actionError', { error: errMsg(statusMutation.error) })}
            </Alert>
          ) : null}

          <Divider />

          {/* Actions row: Pause/Resume + Withdraw + toggle offers */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="text"
              startIcon={
                <Icon name={offersOpen ? 'expand_less' : 'expand_more'} size={18} />
              }
              onClick={() => setOffersOpen((v) => !v)}
            >
              {t('myListings.offers.toggle', { count: listing.offerCount })}
            </Button>

            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
              {isActive || isPaused ? (
                <Button
                  variant="outlined"
                  onClick={() => statusMutation.mutate(isActive ? 'paused' : 'active')}
                  disabled={statusMutation.isPending}
                  startIcon={
                    statusMutation.isPending ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Icon name={isActive ? 'pause' : 'play_arrow'} size={18} />
                    )
                  }
                >
                  {isActive ? t('myListings.pause') : t('myListings.resume')}
                </Button>
              ) : null}

              {!isWithdrawn ? (
                <Button
                  variant="text"
                  color="error"
                  onClick={() => setWithdrawOpen(true)}
                  disabled={statusMutation.isPending}
                >
                  {t('myListings.withdraw')}
                </Button>
              ) : null}
            </Stack>
          </Box>

          {/* Expandable offers-received section */}
          {offersOpen ? <OffersReceived listingId={listing.id} t={t} /> : null}
        </Stack>
      </CardContent>

      {/* Withdraw confirm — irreversible, so confirm before the PATCH. */}
      <Dialog
        open={withdrawOpen}
        onClose={() => {
          if (statusMutation.isPending) return;
          statusMutation.reset();
          setWithdrawOpen(false);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('myListings.withdrawConfirm.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('myListings.withdrawConfirm.body', { product: listing.product })}
          </DialogContentText>
          {statusMutation.isError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t('myListings.actionError', { error: errMsg(statusMutation.error) })}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={() => {
              statusMutation.reset();
              setWithdrawOpen(false);
            }}
            disabled={statusMutation.isPending}
          >
            {t('myListings.withdrawConfirm.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => statusMutation.mutate('withdrawn')}
            disabled={statusMutation.isPending}
            startIcon={statusMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {t('myListings.withdrawConfirm.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

interface OffersReceivedProps {
  listingId: string;
  t: TFunction;
}

/**
 * Offers received on one listing (GET /marketplace/listings/:id/offers). Each
 * pending offer can be Accepted (mints an order — we surface the minted order id
 * inline once `transactionId` comes back) or Rejected. Lazy: the query is only
 * mounted when the seller expands the section.
 */
function OffersReceived({ listingId, t }: OffersReceivedProps) {
  const queryClient = useQueryClient();

  const offersQuery = useQuery({
    queryKey: ['listing-offers', listingId],
    queryFn: ({ signal }) => listListingOffers(listingId, signal),
  });

  function invalidateAll() {
    queryClient.invalidateQueries({ queryKey: ['listing-offers', listingId] });
    // offerCount on the parent listing is unchanged by accept/reject, but the
    // listing may flip to a terminal state on accept — refresh it too.
    queryClient.invalidateQueries({ queryKey: MY_LISTINGS_KEY });
  }

  const offers = useMemo(() => offersQuery.data?.data ?? [], [offersQuery.data]);

  return (
    <Box
      sx={{
        mt: 0.5,
        p: 2,
        borderRadius: 2,
        bgcolor: 'action.hover',
      }}
    >
      <Text variant="title-small" as="h4">
        {t('myListings.offers.heading')}
      </Text>

      <Box sx={{ mt: 1.5 }}>
        {offersQuery.isPending ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        ) : offersQuery.isError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => offersQuery.refetch()}>
                {t('myListings.retry')}
              </Button>
            }
          >
            {t('myListings.offers.loadError', { error: errMsg(offersQuery.error) })}
          </Alert>
        ) : offers.length === 0 ? (
          <Text variant="body-medium" color="text.secondary">
            {t('myListings.offers.empty')}
          </Text>
        ) : (
          <Stack spacing={1.5}>
            {offers.map((offer) => (
              <OfferRow
                key={offer.id}
                offer={offer}
                onChanged={invalidateAll}
                t={t}
              />
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

interface OfferRowProps {
  offer: OfferDto;
  onChanged: () => void;
  t: TFunction;
}

function OfferRow({ offer, onChanged, t }: OfferRowProps) {
  const offerPerKg = microToZarp(offer.offerPriceZarpPerKgMicro);
  const total = microToZarp(offer.totalZarpMicro);
  const isPending = offer.status === 'pending';

  const acceptMutation = useMutation({
    mutationFn: () => acceptOffer(offer.id),
    onSuccess: onChanged,
  });
  const rejectMutation = useMutation({
    mutationFn: () => rejectOffer(offer.id),
    onSuccess: onChanged,
  });

  const busy = acceptMutation.isPending || rejectMutation.isPending;
  // After a successful accept the server returns the offer with its minted
  // order id on `transactionId`; surface it so the seller knows an order exists.
  const mintedOrderId = acceptMutation.data?.transactionId ?? offer.transactionId;

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Text variant="title-small" as="span">
              {offer.buyerName}
            </Text>
            <StatusPill variant={OFFER_PILL[offer.status]}>
              {t(`myListings.offers.status.${offer.status}`)}
            </StatusPill>
          </Stack>
          <Text variant="body-small" color="text.secondary">
            {t('myListings.qty', { qty: offer.qtyKg })}
          </Text>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
            <Money amount={offerPerKg} unit={t('myListings.perKg')} size="sm" />
            <Text variant="label-medium" color="text.secondary">
              {t('myListings.offers.total')}
            </Text>
            <Money amount={total} size="sm" />
          </Stack>
        </Stack>

        {isPending ? (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={() => acceptMutation.mutate()}
              disabled={busy}
              startIcon={
                acceptMutation.isPending ? <CircularProgress size={16} /> : <Icon name="check" size={18} />
              }
            >
              {t('myListings.offers.accept')}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => rejectMutation.mutate()}
              disabled={busy}
              startIcon={
                rejectMutation.isPending ? <CircularProgress size={16} /> : <Icon name="close" size={18} />
              }
            >
              {t('myListings.offers.reject')}
            </Button>
          </Stack>
        ) : null}
      </Box>

      {acceptMutation.isSuccess && mintedOrderId ? (
        <Alert severity="success" sx={{ mt: 1.5 }}>
          {t('myListings.offers.accepted', { order: mintedOrderId.slice(0, 8) })}
        </Alert>
      ) : null}

      {acceptMutation.isError ? (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {t('myListings.actionError', { error: errMsg(acceptMutation.error) })}
        </Alert>
      ) : null}
      {rejectMutation.isError ? (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {t('myListings.actionError', { error: errMsg(rejectMutation.error) })}
        </Alert>
      ) : null}
    </Box>
  );
}
